import type { ReactNode } from 'react';
import { CircleAlertIcon, CircleCheckIcon, InfoIcon, TriangleAlertIcon } from 'lucide-react';

type AlertVariant = 'error' | 'warning' | 'success' | 'info' | 'neutral';

// Literal class names so Tailwind's scanner sees them.
const VARIANT_CLASS: Record<AlertVariant, string> = {
  error: 'alert-error',
  warning: 'alert-warning',
  success: 'alert-success',
  info: 'alert-info',
  neutral: '',
};

const VARIANT_ICON: Record<AlertVariant, ReactNode> = {
  error: <CircleAlertIcon className="size-5 shrink-0" />,
  warning: <TriangleAlertIcon className="size-5 shrink-0" />,
  success: <CircleCheckIcon className="size-5 shrink-0" />,
  info: <InfoIcon className="size-5 shrink-0" />,
  neutral: <InfoIcon className="size-5 shrink-0" />,
};

interface AlertProps {
  variant?: AlertVariant;
  /** Bold first line; `children` then reads as the detail under it. */
  title?: ReactNode;
  children?: ReactNode;
  /** Trailing control (a button or link), kept on the alert's right edge. */
  action?: ReactNode;
  /** Errors interrupt (`alert`); everything else is announced politely (`status`). */
  role?: 'alert' | 'status';
  className?: string;
}

/**
 * System feedback (docs/brand.md → Roles): a soft tint of the semantic colour, an icon,
 * and text in the colour itself. Never use it for HTTP status — those have their own tokens.
 */
export function Alert({ variant = 'neutral', title, children, action, role, className = '' }: AlertProps) {
  return (
    <div
      role={role ?? (variant === 'error' ? 'alert' : 'status')}
      // flex, not daisyUI's grid, so the text takes the free width and `action` sits at the edge.
      className={`alert alert-soft ${VARIANT_CLASS[variant]} flex items-center gap-3 text-sm ${className}`}
    >
      {VARIANT_ICON[variant]}
      {/* Only stretch when there's an action to push right, so `justify-center` still centres. */}
      <div className={`min-w-0 ${action ? 'flex-1' : ''}`}>
        {title ? (
          <>
            <p className="font-medium">{title}</p>
            {children && <div className="text-xs mt-0.5">{children}</div>}
          </>
        ) : (
          children
        )}
      </div>
      {action && <div className="flex items-center gap-2 shrink-0">{action}</div>}
    </div>
  );
}
